import { create } from "zustand";
import type { PlaceType } from "./api";

type EditorMode = "viewing" | "placing" | "linking";

export interface PlacingContext {
  /** The type of place being added (optional until user picks) */
  type?: PlaceType;
  /** Coordinates (set when user clicks map or from POI/search) */
  coordinates?: [number, number];
  /** Optional name (from POI or search) */
  name?: string;
}

export interface LinkingContext {
  /** The place to add the related place to */
  sourcePlaceId: string;
  /** Place types to filter by (null = show all) */
  allowedTypes: PlaceType[] | null;
  /** Category label for display */
  categoryLabel: string;
  /** Coordinates for creating a new place inline */
  coordinates?: [number, number];
  /** Optional name to pre-fill in the form */
  name?: string;
}

interface EditorModeState {
  /** Current editor mode */
  mode: EditorMode;

  /** Context for placing mode (type, coordinates, name) */
  placingContext: PlacingContext | null;

  /** Context for linking mode (source place, allowed types) */
  linkingContext: LinkingContext | null;

  /** Enter placing mode with optional initial context */
  startPlacing: (context?: PlacingContext) => void;

  /** Set/update the placing context */
  setPlacingContext: (context: PlacingContext) => void;

  /** Exit placing mode */
  cancelPlacing: () => void;

  /** Enter linking mode to add a related place */
  startLinking: (context: LinkingContext) => void;

  /** Set/update the linking context */
  setLinkingContext: (context: Partial<LinkingContext>) => void;

  /** Exit linking mode */
  cancelLinking: () => void;

  /** Reset to viewing mode (also clears context) */
  reset: () => void;
}

export const useEditorMode = create<EditorModeState>((set) => ({
  mode: "viewing",
  placingContext: null,
  linkingContext: null,

  startPlacing: (context) => {
    set({
      mode: "placing",
      placingContext: context ?? {},
      linkingContext: null,
    });
  },

  setPlacingContext: (context) => {
    set((state) => ({
      placingContext: state.placingContext ? { ...state.placingContext, ...context } : context,
    }));
  },

  cancelPlacing: () => {
    set({
      mode: "viewing",
      placingContext: null,
    });
  },

  startLinking: (context) => {
    set({
      mode: "linking",
      linkingContext: context,
      placingContext: null,
    });
  },

  setLinkingContext: (context) => {
    set((state) => ({
      linkingContext: state.linkingContext ? { ...state.linkingContext, ...context } : null,
    }));
  },

  cancelLinking: () => {
    set({
      mode: "viewing",
      linkingContext: null,
    });
  },

  reset: () => {
    set({
      mode: "viewing",
      placingContext: null,
      linkingContext: null,
    });
  },
}));
